import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { ToggleStatCard } from '@/components/ui/ToggleStatCard';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { RichTextDisplay } from '@/components/ui/RichTextEditor';

/** Strip CRLF characters to prevent log forging (CWE-117) */
function sanitizeForLog(input: string): string {
  return input.replace(/[\r\n]+/g, ' ');
}
import { incidentsService, Incident as ApiIncident, IncidentStats } from '../services/incidents.service';
import { usersService, User } from '../services/users.service';
import { SECURITY_CATEGORIES } from '@corporate-security/types';

// Use the incident interface from the service
type Incident = ApiIncident;

// Static color configurations - defined outside component to avoid recreation
const SEVERITY_COLORS = {
  success: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
  warning: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
  danger: 'bg-danger-100 text-danger-800 border-danger-200 dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800',
  secondary: 'bg-secondary-100 text-secondary-800 border-secondary-200 dark:bg-secondary-900/20 dark:text-secondary-300 dark:border-secondary-800',
} as const;

const STATUS_COLORS = {
  success: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  danger: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  secondary: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
} as const;

const STATUS_ICONS = {
  OPEN: ExclamationTriangleIcon,
  IN_PROGRESS: ClockIcon,
  RESOLVED: CheckCircleIcon,
  PENDING_CLOSURE_APPROVAL: ClockIcon,
  CLOSED: XCircleIcon,
  CANCELLED: XCircleIcon,
} as const;

// Animation variants - defined outside component to avoid recreation
const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05, // Reduced from 0.1 for faster animation
    },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 }, // Reduced y from 20 for subtler animation
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' }, // Reduced from 0.4
  },
};

function IncidentCard({ incident, usersMap }: { incident: Incident; usersMap: Map<string, User> }) {
  const severityConfig = incidentsService.getSeverityConfig(incident.severity);
  const statusConfig = incidentsService.getStatusConfig(incident.status);

  // Get assignee display name
  const getAssigneeDisplayName = () => {
    // First try to use the assignee object if it exists (from backend)
    if (incident.assignee) {
      return `${incident.assignee.firstName} ${incident.assignee.lastName}`;
    }
    // Fall back to users map lookup
    if (incident.assignedTo) {
      if (usersMap.has(incident.assignedTo)) {
        const user = usersMap.get(incident.assignedTo);
        return user?.fullName ?? 'Unknown';
      }
      // We have an assignee ID but couldn't load the user
      return 'Unknown';
    }
    return 'Unassigned';
  };

  const StatusIcon = STATUS_ICONS[incident.status] || ExclamationTriangleIcon;

  return (
    <Link to={`/security/incidents/${incident.id}`} className="block">
      <Card hover="default" padding="none" className="transition-all duration-200 cursor-pointer">
          <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="font-semibold text-lg text-secondary-900 dark:text-white truncate">
                  {incident.title}
                </h3>
                <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400">
                  {incident.incidentNumber}
                </span>
              </div>
              <RichTextDisplay
                content={incident.description}
                className="text-secondary-600 dark:text-secondary-400 text-sm mb-3 line-clamp-2 [&_p]:m-0"
                emptyMessage="No description"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <span className={cn('px-3 py-1 text-xs font-medium rounded-full border', SEVERITY_COLORS[severityConfig.color as keyof typeof SEVERITY_COLORS])}>
              {severityConfig.label}
            </span>
            <span className={cn('px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1', STATUS_COLORS[statusConfig.color as keyof typeof STATUS_COLORS])}>
              <StatusIcon className="w-3 h-3" />
              <span>{statusConfig.label}</span>
            </span>
            {incident.category && (
              <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full">
                {incident.category}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="flex items-center space-x-2">
              <UserIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
              <span className="text-secondary-600 dark:text-secondary-400">Reporter:</span>
              <span className="font-medium text-secondary-900 dark:text-white">
                {incident.reporterName ?? 'Unknown'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
              <span className="text-secondary-600 dark:text-secondary-400">Updated:</span>
              <span className="font-medium text-secondary-900 dark:text-white">
                {incidentsService.formatTimestamp(incident.updatedAt)}
              </span>
            </div>
            {incident.assignedTo && (
              <div className="flex items-center space-x-2">
                <UserIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                <span className="text-secondary-600 dark:text-secondary-400">Assignee:</span>
                <span className="font-medium text-secondary-900 dark:text-white">
                  {getAssigneeDisplayName()}
                </span>
              </div>
            )}
            {incident.location && (
              <div className="flex items-center space-x-2">
                <span className="text-secondary-600 dark:text-secondary-400">Location:</span>
                <span className="font-medium text-secondary-900 dark:text-white">
                  {incident.location}
                </span>
              </div>
            )}
            {incident.occurredAt && (
              <div className="flex items-center space-x-2">
                <CalendarIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                <span className="text-secondary-600 dark:text-secondary-400">Occurred:</span>
                <span className="font-medium text-secondary-900 dark:text-white">
                  {incidentsService.formatTimestamp(incident.occurredAt)}
                </span>
              </div>
            )}
          </div>
          </CardContent>
        </Card>
      </Link>
  );
}

export default function Incidents() {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>(searchParams.get('severity') ?? 'all');
  const [selectedStatus, setSelectedStatus] = useState<string>(searchParams.get('status') ?? 'all');
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') ?? 'all');
  const [showOnlyMyApprovals, setShowOnlyMyApprovals] = useState(false);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [stats, setStats] = useState<IncidentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, _setCurrentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map());

  const fetchIncidents = useCallback(async () => {
    try {
      setLoading(true);
      const [incidentsData, statsData] = await Promise.all([
        incidentsService.getIncidents({
          page: currentPage,
          limit: 20,
          severity: selectedSeverity !== 'all' ? selectedSeverity : undefined,
          status: showOnlyMyApprovals ? 'PENDING_CLOSURE_APPROVAL' : (selectedStatus !== 'all' ? selectedStatus : undefined),
          search: searchQuery ?? undefined,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          supervisorId: showOnlyMyApprovals ? 'me' : undefined,
        }),
        incidentsService.getIncidentStats()
      ]);

      setIncidents(incidentsData.incidents);
      setTotalPages(incidentsData.totalPages);
      setStats(statsData);
      setError(null);

      // Fetch users for assignees that don't have the assignee object populated
      const assigneeIds = incidentsData.incidents
        .filter(inc => inc.assignedTo && !inc.assignee)
        .map(inc => inc.assignedTo as string);

      const uniqueAssigneeIds = [...new Set(assigneeIds)];

      if (uniqueAssigneeIds.length > 0) {
        // Fetch each assignee user by ID (more reliable than filtering all users)
        const newUsersMap = new Map<string, User>();
        await Promise.all(
          uniqueAssigneeIds.map(async (userId) => {
            try {
              const user = await usersService.getUserById(userId);
              newUsersMap.set(userId, user);
            } catch (err) {
              console.error(`Failed to fetch user ${sanitizeForLog(userId)}:`, err);
              // Continue without this user - will show fallback
            }
          })
        );
        setUsersMap(newUsersMap);
      }
    } catch (err) {
      console.error('Failed to fetch incidents:', err instanceof Error ? sanitizeForLog(err.message) : err);
      setError('Failed to load incidents');
      setIncidents([]);
      setStats({
        total: 0,
        open: 0,
        inProgress: 0,
        resolved: 0,
        high: 0,
        bySeverity: {},
        byStatus: {}
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedSeverity, selectedStatus, searchQuery, showOnlyMyApprovals]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const filteredIncidents = incidents;

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Incident Management
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Monitor and manage security incidents across your organization
            </p>
          </div>
          <Link to="/security/incidents/create">
            <Button className="w-full sm:w-auto">
              <PlusIcon className="w-4 h-4 mr-2" />
              Report Incident
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <ToggleStatCard
            label="Total Incidents"
            active={selectedStatus === 'all' && selectedSeverity === 'all'}
            onToggle={() => {
              setSelectedStatus('all');
              setSelectedSeverity('all');
            }}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                {stats?.total ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Total Incidents
              </div>
            </CardContent>
          </ToggleStatCard>
          <ToggleStatCard
            label="Open"
            active={selectedStatus === 'OPEN'}
            activeRingClass="ring-danger-500"
            onToggle={() => setSelectedStatus(selectedStatus === 'OPEN' ? 'all' : 'OPEN')}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-danger-600 dark:text-danger-400">
                {stats?.open ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Open
              </div>
            </CardContent>
          </ToggleStatCard>
          <ToggleStatCard
            label="In Progress"
            active={selectedStatus === 'IN_PROGRESS'}
            activeRingClass="ring-warning-500"
            onToggle={() => setSelectedStatus(selectedStatus === 'IN_PROGRESS' ? 'all' : 'IN_PROGRESS')}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">
                {stats?.inProgress ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                In Progress
              </div>
            </CardContent>
          </ToggleStatCard>
          <ToggleStatCard
            label="High Severity"
            active={selectedSeverity === 'HIGH'}
            activeRingClass="ring-danger-500"
            onToggle={() => setSelectedSeverity(selectedSeverity === 'HIGH' ? 'all' : 'HIGH')}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-danger-600 dark:text-danger-400">
                {stats?.high ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                High Severity
              </div>
            </CardContent>
          </ToggleStatCard>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div variants={ITEM_VARIANTS}>
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search incidents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:w-auto">
                <Button
                  variant={showOnlyMyApprovals ? 'default' : 'outline'}
                  onClick={() => setShowOnlyMyApprovals((v) => !v)}
                >
                  Pending My Approval
                </Button>
                <select
                  value={selectedSeverity}
                  onChange={(e) => setSelectedSeverity(e.target.value)}
                  className="h-10 pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Severities</option>
                  <option value="HIGH">High</option>
                  <option value="MODERATE">Moderate</option>
                  <option value="MINIMAL">Minimal</option>
                </select>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="h-10 pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="RESOLVED">Resolved</option>
                  <option value="CLOSED">Closed</option>
                </select>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="h-10 pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Categories</option>
                  {SECURITY_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Incidents List */}
      <motion.div variants={ITEM_VARIANTS}>
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-lg text-secondary-600 dark:text-secondary-400">
                Loading incidents...
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 dark:text-danger-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                Error Loading Incidents
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                {error}
              </p>
              <Button onClick={fetchIncidents} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : filteredIncidents.length > 0 ? (
          <div className="space-y-4">
            {filteredIncidents.map((incident) => (
              <IncidentCard
                key={incident.id}
                incident={incident}
                usersMap={usersMap}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                {showOnlyMyApprovals ? 'No pending approvals — nice work' : 'No incidents found'}
              </h3>
              {!showOnlyMyApprovals && (
                <p className="text-secondary-600 dark:text-secondary-400">
                  No incidents match your current filters. Try adjusting your search criteria.
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </motion.div>
    </motion.div>
  );
}